import type { LoginCredentials, LoginResponse, User, UserRole, WatchmanLoginResponse } from "../type";
import { getOspreyApiBaseUrl } from "../../../shared/utils/apiBase";

// Admin login goes through osprey-api's iam-proxy (<base>/iam/auth/login), which
// forwards to internal watchman (incl. /auth/me below). The Osprey UI never calls
// watchman directly — single origin (osprey-api). See apiBase.ts.
const getWatchmanUrl = (): string => getOspreyApiBaseUrl() + '/iam';

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
  );
  return JSON.parse(json);
}

export const authApiClient = {
  login: async (data: LoginCredentials): Promise<LoginResponse> => {
    const url = getWatchmanUrl();
    const res = await fetch(`${url}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email: data.email, password: data.password }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const message = err.message || err.errors?.[0]?.message || res.statusText || 'Login failed';
      throw { status: res.status, message, errors: err.errors, fullError: err };
    }

    const { accessToken }: WatchmanLoginResponse = await res.json();
    const payload = decodeJwtPayload(accessToken);

    // The watchman JWT only carries { sub, roleName, roleScope, tenantId, merchantId, p }.
    // email/fullName/permissions are NOT in the token — they were always undefined here
    // (blank user name in the header, empty permission map). /auth/me reads them from the
    // DB and returns the DECODED permission map, so hydrate from it (best-effort).
    let me: Record<string, unknown> = {};
    try {
      const meRes = await fetch(`${url}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
      });
      if (meRes.ok) me = await meRes.json();
    } catch {
      // best-effort — fall back to token claims below
    }

    const user: User = {
      id: (me.userId as string) ?? (payload.sub as string),
      email: (me.email as string) ?? '',
      fullName: (me.fullName as string) ?? '',
      roleName: (me.roleName as UserRole) ?? (payload.roleName as UserRole),
      roleScope: (me.roleScope as string) ?? (payload.roleScope as string),
      merchantId:
        ((me.merchantId ?? me.tenantId ?? payload.tenantId ?? payload.merchantId) as string | null) ?? null,
      isProfileCompleted: (me.isProfileCompleted as boolean) ?? false,
      permissions: (me.permissions as Record<string, string[]>) ?? {},
    };

    return { token: accessToken, user };
  },
};
